import React, {memo} from 'react'
import styled from 'styled-components'
import themeGet from '@fuegokit/react/utils/theme-get'
import Box from '@fuegokit/react/components/Box'
import type {SVGProps, Size} from './types'

const themedBackground = {
  light: themeGet('colors.elevation.surface.default.[default]'), // '#FFFFFF'
  dark: themeGet('colors.elevation.surface.default.[default]') // '#1B2638')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any

/**
 * Returns the background color depending on the passed through mode.
 * @param mode
 */
export const getBackground = (mode = 'light') => themedBackground[mode]

export const sizes: Record<Size, string> = {
  small: '16px',
  medium: '24px',
  large: '32px',
  xlarge: '48px'
}

export const sizeMap: Record<Size, Size> = {
  small: 'small',
  medium: 'medium',
  large: 'large',
  xlarge: 'xlarge'
}

const smallStyles = {
  width: sizes.small,
  height: sizes.small
}
const mediumStyles = {
  width: sizes.medium,
  height: sizes.medium
}
const largeStyles = {
  width: sizes.large,
  height: sizes.large
}
const xlargeStyles = {
  width: sizes.xlarge,
  height: sizes.xlarge
}

// pre-built css style-size map
export const sizeStyleMap = {
  small: smallStyles,
  medium: mediumStyles,
  large: largeStyles,
  xlarge: xlargeStyles
} as const
/**
 * __SVG__
 *
 * An icon is used as a visual representation of common actions and commands to provide context.
 *
 * - [Examples](https://atlaskit.atlassian.com/packages/design-system/icon)
 * - [Code](https://atlaskit.atlassian.com/packages/design-system/icon/docs/custom-svgs)
 */

// eslint-disable-next-line no-lone-blocks
{
  /* <Box
sx={{
  overflow: 'hidden',
  pointerEvents: 'none',
  stopColor: 'currentColor',
  color: primaryColor,
  fill: secondaryColor,
  ...sizeStyleMap[size]
}}
>  </Box> */
}
export const AtlasSvgPrimitive = memo(function AtlasSvgPrimitive({
  size = 'medium',
  label,
  primaryColor = 'currentColor',
  secondaryColor,
  testId,
  children
}: SVGProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      style={{
        color: primaryColor,
        fill: secondaryColor,
        ...sizeStyleMap[size]
      }}
      data-testid={testId}
      aria-label={label || undefined}
      role={label ? 'img' : 'presentation'}
    >
      {children}
    </svg>
  )
})

const Wrapper = styled(Box)`
  --local-background: var(--ds-background-input, #fafbfc);
  --local-background-active: var(--ds-background-input-pressed, #deebff);
  --local-background-checked: var(--ds-background-selected-bold, #0052cc);
  --local-background-checked-hover: var(--ds-background-selected-bold-hovered, #0065ff);
  --local-background-disabled: var(--ds-background-disabled, #f4f5f7);
  --local-background-hover: var(--ds-background-input-hovered, #ebecf0);
  --local-border: var(--ds-border-input, #dfe1e6);
  --local-border-active: var(--ds-border, #deebff);
  --local-border-checked: var(--ds-background-selected-bold, #0052cc);
  --local-border-checked-hover: var(--ds-background-selected-bold-hovered, #0065ff);
  --local-border-checked-invalid: var(--ds-border-danger, #ff5630);
  --local-border-disabled: var(--ds-background-disabled, #f4f5f7);
  --local-border-focus: var(--ds-border-focused, #4c9aff);
  --local-border-hover: var(--ds-border-input, #dfe1e6);
  --local-border-invalid: var(--ds-border-danger, #ff5630);
  --local-tick-active: var(--ds-icon-inverse, #0052cc);
  --local-tick-checked: var(--ds-icon-inverse, #fafbfc);
  --local-tick-disabled: var(--ds-icon-disabled, #a5adba);
  --local-tick-rest: transparent;

  --checkbox-background-color: var(--local-background-checked);
  --checkbox-border-color: var(--local-border-checked);
  --checkbox-tick-color: var(--local-tick-checked);
`
